import React from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as queryString from 'query-string';
import ReactGA from 'react-ga4';
import { Box, Container, Typography, Button, Link } from '@mui/material';
import { ArrowDownward, Facebook, Twitter, LinkedIn } from '@mui/icons-material';
import { UserTypeEnum } from 'enums';
import Testimonials from 'components/Testimonials';
import EmbedVideo from 'components/EmbedVideo';
import Steps from '../Steps';
import Faq from '../Faq';
import { useStyles } from './styles';
import LazyLoad from 'react-lazyload';
import { getEnv } from 'utils';
import { useAuthUser } from 'features/auth/AuthUserContext';

export default function FriendsReferralContent() {
  const classes = useStyles();
  const router = useRouter();
  const { user, isAuthenticated } = useAuthUser();
  const isFeatureAvailable =
    !isAuthenticated ||
    (![UserTypeEnum.ADMIN, UserTypeEnum.LAB_RESEARCHER].includes(user.tp_user) && user.team === 1);

  function sendClickTrackingEvents() {
    ReactGA.event({
      category: 'landing_page',
      action: 'friends_referral_clicked',
      label: 'Friends referral clicked',
      value: user.id,
    });
  }

  function makeInvitesUrl() {
    if (isAuthenticated) {
      return '/my-account/invites';
    }

    return `/auth/login-register?${queryString.stringify({
      from_url: router.basePath + router.pathname,
      redirect: `${getEnv('MTG_URL_APP')}${router.basePath}/my-account/invites`,
    })}`;
  }

  function makeShareURL() {
    return getEnv('MTG_URL_APP') + router.basePath + router.pathname;
  }

  return (
    <>
      <Box className={classes.greyBg} py={6}>
        <Container>
          {!isFeatureAvailable && (
            <div className={classes.notAvailableAlert}>
              <Typography>
                <b className={classes.primaryColor}>
                  This feature is not available for your current plan.
                </b>
              </Typography>
            </div>
          )}

          <div className={classes.header}>
            <div className={classes.headerTextBox}>
              <Typography className={classes.secondaryColor} variant="h3">
                <b>
                  INVITE YOUR <br /> FRIENDS
                </b>
              </Typography>

              <div className={classes.headerSubtext}>
                <Typography variant="h6">
                  and get up to a free <br /> month subscription
                </Typography>

                <ArrowDownward />
              </div>
            </div>

            <img
              src="/images/friends-referral.svg"
              loading="lazy"
              width="500"
              height="389"
              alt="Mind the Graph Friends Referral"
            />
          </div>
        </Container>
      </Box>

      <Container>
        <div className={classes.stepsBox}>
          <Typography variant="h5" align="center">
            <b>How it Works</b>
          </Typography>

          <Steps classes={{ root: classes.steps, numberBox: classes.stepsNumberBox }} />

          {isFeatureAvailable && (
            <Box textAlign="center">
              <NextLink passHref={true} href={makeInvitesUrl()}>
                <Button
                  className={classes.button}
                  color="primary"
                  variant="contained"
                  onClick={sendClickTrackingEvents}
                >
                  Invite Your Friends Now!
                </Button>
              </NextLink>
            </Box>
          )}
          <Box textAlign="center">
            <Link
              target="_blank"
              href={`https://www.facebook.com/sharer/sharer.php?u=${makeShareURL()}`}
              underline="hover"
            >
              <Facebook className={classes.sharedIcons} />
            </Link>
            <Link
              target="_blank"
              href={`https://twitter.com/intent/tweet?text=${makeShareURL()}`}
              underline="hover"
            >
              <Twitter className={classes.sharedIcons} />
            </Link>
            <Link
              target="_blank"
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${makeShareURL()}`}
              underline="hover"
            >
              <LinkedIn className={classes.sharedIcons} />
            </Link>
          </Box>
        </div>
      </Container>

      <LazyLoad offset={100}>
        <Box mt={6} py={6} bgcolor="grey.200">
          <Container>
            <div className={classes.videoBox}>
              <div>
                <img
                  src="/images/gift-icon-primary.svg"
                  loading="lazy"
                  height="92"
                  width="99"
                  alt="Gift icon"
                />

                <Typography className={classes.primaryColor} variant="h5">
                  <b>
                    Invite your friends <br /> and collect rewards <br /> together.
                  </b>
                </Typography>
              </div>

              <EmbedVideo
                classes={{ root: classes.video }}
                width="500"
                url="https://www.youtube.com/embed/zw93IykwhtM"
              />
            </div>
          </Container>
        </Box>
      </LazyLoad>

      <LazyLoad offset={100}>
        <Box py={6}>
          <Container>
            <Box mb={4} textAlign="center">
              <Typography className={classes.primaryColor} variant="h5">
                <b>See what people are talking about Mind the Graph</b>
              </Typography>
            </Box>

            <Testimonials
              actionButton={{
                href: makeInvitesUrl(),
                label: 'Start Now!',
                disabled: !isFeatureAvailable,
                onClick: sendClickTrackingEvents,
              }}
            />
          </Container>
        </Box>
      </LazyLoad>

      <LazyLoad offset={100}>
        <Box className={classes.greyBg} py={6}>
          <Container>
            <Faq />
          </Container>
        </Box>
      </LazyLoad>

      <LazyLoad offset={100}>
        <Box className={classes.primaryBg} py={6} textAlign="center">
          <Container>
            <Typography variant="h5">
              <b>Ready to Start Now</b>
            </Typography>

            <Box mt={4}>
              <Typography variant="h6">
                And get up to a 100% discount on your <br /> subscriptions fees for the following
                month.
              </Typography>
            </Box>

            {isFeatureAvailable && (
              <Box mt={4}>
                <NextLink passHref={true} href={makeInvitesUrl()}>
                  <Button
                    className={classes.button}
                    color="primary"
                    variant="contained"
                    onClick={sendClickTrackingEvents}
                  >
                    Invite Your Friends Now!
                  </Button>
                </NextLink>
              </Box>
            )}
          </Container>
        </Box>
      </LazyLoad>
    </>
  );
}
